import { Feature } from '@/components/shared/constants/features';

import { usePermissions } from '.';
import { usePermissionDeniedDialog } from './PermissionDeniedDialogProvider';

export interface PermissionGateProps {
    disabled: boolean;
    title: string | undefined;
    /**
     * Wraps a handler so it only fires when the user has the feature. If the
     * user lacks it, the shared permission-denied dialog is shown instead.
     * Use this for "looks-disabled-but-clickable" controls that want to
     * explain the gate via a dialog rather than silently no-op.
     */
    gateAction: <Args extends unknown[]>(
        handler: ((...args: Args) => void) | undefined,
    ) => (...args: Args) => void;
}

export const usePermissionGate = (
    feature: Feature,
    deniedMessage: string,
    allowedByResource?: boolean,
): PermissionGateProps => {
    const { hasFeature } = usePermissions();
    const { show } = usePermissionDeniedDialog();
    const hasFeatureAccess = hasFeature(feature);
    const allowed = hasFeatureAccess || Boolean(allowedByResource);

    const gateAction =
        <Args extends unknown[]>(handler: ((...args: Args) => void) | undefined) =>
        (...args: Args) => {
            if (!allowed) {
                show(deniedMessage);
                return;
            }
            handler?.(...args);
        };

    return {
        disabled: !allowed,
        title: allowed ? undefined : deniedMessage,
        gateAction,
    };
};
